import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Header from './../Common/Header';
import Footer from './../Common/Footer';
import Banner from './../Elements/Banner';
import ImageGallery from 'react-image-gallery';
import SiteContentContext from '../../content/SiteContentContext';
import { getByPath } from '../../content/siteContentUtils';
import '../../../node_modules/react-image-gallery/styles/css/image-gallery.css';

const fallbackImages = [
    {
        original: require('./../../images/products/pic-1.jpg'),
        thumbnail: require('./../../images/products/thumb/pic-1.jpg'),
    },
    {
        original: require('./../../images/products/pic-2.jpg'),
        thumbnail: require('./../../images/products/thumb/pic-2.jpg'),
    },
    {
        original: require('./../../images/products/pic-3.jpg'),
        thumbnail: require('./../../images/products/thumb/pic-3.jpg'),
    },
    {
        original: require('./../../images/products/pic-4.jpg'),
        thumbnail: require('./../../images/products/thumb/pic-4.jpg'),
    },
    {
        original: require('./../../images/products/pic-5.jpg'),
        thumbnail: require('./../../images/products/thumb/pic-5.jpg'),
    },
];

const defaultRelatedProducts = [
    {
        image: require('./../../images/products/pic-1.jpg'),
        title: 'Modern Daire Kapisi',
        code: 'DGK-001',
        description: 'Daire girislerinde guvenlik ve estetik ihtiyacina uygun model.',
        sale: false,
    },
    {
        image: require('./../../images/products/pic-2.jpg'),
        title: 'Villa Giris Kapisi',
        code: 'VKM-001',
        description: 'Villa projeleri icin ozel olcu ve tasarim secenekleri sunar.',
        sale: true,
    },
    {
        image: require('./../../images/products/pic-3.jpg'),
        title: 'Lake Ic Kapi',
        code: 'ICK-001',
        description: 'Ic mekanlarda sade ve modern bir gorunum saglar.',
        sale: false,
    },
    {
        image: require('./../../images/products/pic-4.jpg'),
        title: 'Bina Giris Kapisi',
        code: 'BGK-001',
        description: 'Apartman ve site girisleri icin dayanikli ve sik cozum.',
        sale: false,
    },
];

const productDetailBannerImage = 'https://mujjkl4gyemnmfv2.public.blob.vercel-storage.com/detaybanner.jpg';

const defaultProductDetail = {
    image: require('./../../images/products/pic-1.jpg'),
    title: 'Modern Daire Kapisi',
    code: 'DGK-001',
    description: 'Daire girislerinde guvenlik ve estetik ihtiyacina uygun model.',
};

const categoryLabels = {
    apartment: 'Daire Giris Kapilari',
    villa: 'Villa Kapi Modelleri',
    building: 'Bina Giris Kapilari',
    interior: 'Ic Kapilar',
};
const productCategoryKeys = Object.keys(categoryLabels);

const normalizeCompareValue = (value) =>
    String(value || '')
        .trim()
        .toLowerCase();

const normalizeProductImageUrls = (item = {}) => {
    const configuredImages = Array.isArray(item.imageUrls) ? item.imageUrls : [];
    const cleanedConfiguredImages = configuredImages
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter(Boolean);

    if (cleanedConfiguredImages.length > 0) {
        return cleanedConfiguredImages;
    }

    const singleImage =
        (typeof item.image === 'string' && item.image.trim()) ||
        (typeof item.imageUrl === 'string' && item.imageUrl.trim()) ||
        '';

    return singleImage ? [singleImage] : [];
};

const normalizeProduct = (item = {}) => {
    const imageUrls = normalizeProductImageUrls(item);
    const primaryImage = imageUrls[0] || defaultProductDetail.image;

    return {
        image: primaryImage,
        imageUrls: imageUrls.length ? imageUrls : [primaryImage],
        title: item.title || item.name || defaultProductDetail.title,
        code: item.code || defaultProductDetail.code,
        description: item.description || defaultProductDetail.description,
        sale: Boolean(item.sale),
    };
};

const isSameProduct = (left = {}, right = {}) => {
    const leftCode = normalizeCompareValue(left.code);
    const rightCode = normalizeCompareValue(right.code);

    if (leftCode && rightCode) {
        return leftCode === rightCode;
    }

    const leftTitle = normalizeCompareValue(left.title || left.name);
    const rightTitle = normalizeCompareValue(right.title || right.name);

    return Boolean(leftTitle && rightTitle && leftTitle === rightTitle);
};

const findConfiguredProduct = (content, categoryKey, product) => {
    const preferredCategoryKeys = [
        categoryKey,
        ...productCategoryKeys.filter((key) => key !== categoryKey),
    ];

    for (const key of preferredCategoryKeys) {
        const rows = getByPath(content, `productCategories.${key}.items`, []);
        if (!Array.isArray(rows)) {
            continue;
        }

        const matched = rows.find((item) => isSameProduct(item, product));
        if (matched) {
            return matched;
        }
    }

    return null;
};

const mergeRouteProduct = (routeProduct, configuredProduct) => {
    const normalizedRouteProduct = normalizeProduct(routeProduct);

    if (!configuredProduct) {
        return normalizedRouteProduct;
    }

    const normalizedConfiguredProduct = normalizeProduct(configuredProduct);
    const resolvedImageUrls =
        normalizedRouteProduct.imageUrls.length > 1
            ? normalizedRouteProduct.imageUrls
            : normalizedConfiguredProduct.imageUrls.length
                ? normalizedConfiguredProduct.imageUrls
                : normalizedRouteProduct.imageUrls;
    const primaryImage =
        resolvedImageUrls[0] ||
        normalizedRouteProduct.image ||
        normalizedConfiguredProduct.image ||
        defaultProductDetail.image;

    return {
        ...normalizedConfiguredProduct,
        ...normalizedRouteProduct,
        image: primaryImage,
        imageUrls: resolvedImageUrls.length ? resolvedImageUrls : [primaryImage],
    };
};

const buildGalleryItems = (imageUrls = []) => {
    if (!Array.isArray(imageUrls) || imageUrls.length === 0) {
        return fallbackImages;
    }

    return imageUrls.map((imageUrl) => ({
        original: imageUrl,
        thumbnail: imageUrl,
    }));
};

const defaultWhatsappPhone = '905301790458';

const toWhatsappPhone = (value = '') => String(value).replace(/\D/g, '');

const buildWhatsappLink = (phone, product) => {
    const whatsappPhone = toWhatsappPhone(phone) || defaultWhatsappPhone;
    const productTitle = product?.title || 'ürün';
    const productCode = product?.code ? ` (${product.code})` : '';
    const message = `Merhaba, ${productTitle}${productCode} ürünü ile ilgili bilgi almak istiyorum.`;

    return `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(message)}`;
};

class ShopDetail extends React.Component {
    static contextType = SiteContentContext;

    componentDidMount() {
        function loadScript(src) {
            return new Promise(function (resolve, reject) {
                var script = document.createElement('script');
                script.src = src;
                script.addEventListener('load', function () {
                    resolve();
                });
                script.addEventListener('error', function (e) {
                    reject(e);
                });
                document.body.appendChild(script);
                document.body.removeChild(script);
            });
        }

        loadScript('./assets/js/custom.js');
    }

    render() {
        const content = this.context?.content || {};
        const routeState = this.props.location?.state || {};
        const categoryKey = routeState.categoryKey || 'apartment';
        const hasRouteProduct =
            routeState.product &&
            typeof routeState.product === 'object' &&
            Object.keys(routeState.product).length > 0;
        const configuredProduct = hasRouteProduct
            ? findConfiguredProduct(content, categoryKey, routeState.product)
            : null;
        const selectedProduct = mergeRouteProduct(routeState.product, configuredProduct);
        const bannerImage = productDetailBannerImage;
        const phones = getByPath(content, 'contact.phones', []);
        const whatsappPhone = Array.isArray(phones) && phones.length > 0 ? phones[0] : '';
        const whatsappLink = buildWhatsappLink(whatsappPhone, selectedProduct);
        const categoryLabel = categoryLabels[categoryKey] || 'Celik Kapi';
        const galleryItems = buildGalleryItems(selectedProduct.imageUrls);
        const bannerDescription = `${selectedProduct.title} modelinin teknik ve görsel detaylarını bu sayfada inceleyebilirsiniz.`
            .toLocaleUpperCase('tr-TR');

        const routeRelatedProducts = Array.isArray(routeState.relatedProducts) ? routeState.relatedProducts : [];
        const normalizedRelatedProducts = (routeRelatedProducts.length > 0 ? routeRelatedProducts : defaultRelatedProducts)
            .map((item) => normalizeProduct(item))
            .filter((item) => item.title !== selectedProduct.title || item.code !== selectedProduct.code)
            .slice(0, 4);

        return (
            <>
                <Header />
                <div className="page-content">
                    <Banner
                        className="product-detail-banner"
                        title="ÜRÜN DETAYI"
                        pagename="ÜRÜNLER"
                        description={bannerDescription}
                        bgimage={bannerImage}
                        showOverlay={false}
                    />
                    <div className="section-full p-t80 p-b50 mobile-page-padding">
                        <div className="container woo-entry">
                            <div className="row m-b30">
                                <div className="col-md-5 col-sm-12  m-b30">
                                    <div className="sx-box sx-product-gallery on-show-slider">
                                        <ImageGallery items={galleryItems} showPlayButton={false} showFullscreenButton={false} />
                                    </div>
                                </div>
                                <div className="col-md-7 col-sm-12">
                                    <div className="product-detail-info">
                                        <div className="single-product-title ">
                                            <h3 className="post-title"><NavLink to="#">{selectedProduct.title}</NavLink></h3>
                                            <p className="m-b0 product-code-text">
                                                {selectedProduct.code || '-'}
                                            </p>
                                        </div>
                                        <div className="wt-product-text">
                                            <p>{selectedProduct.description}</p>
                                        </div>
                                        <div className="product_meta">
                                            <div className="sku_wrapper">Ürün Kodu:
                                                <span className="sku">{selectedProduct.code || '-'}</span>
                                            </div>
                                            <div className="posted_in">Kategori:
                                                <NavLink to="#" rel="tag">{categoryLabel}</NavLink>
                                            </div>
                                        </div>
                                        <div className="m-t20">
                                            <a
                                                href={whatsappLink}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="whatsapp-cta-button"
                                                aria-label="WhatsApp ile bilgi al"
                                            >
                                                <span className="whatsapp-cta-icon-wrap">
                                                    <i className="fa fa-whatsapp whatsapp-cta-icon" />
                                                </span>
                                                <span className="whatsapp-cta-content">
                                                    <span className="whatsapp-cta-title">WhatsApp ile Bilgi Al</span>
                                                    <span className="whatsapp-cta-subtitle">Hızlı dönüş için tıklayın</span>
                                                </span>
                                                <span className="whatsapp-cta-arrow">
                                                    <i className="fa fa-angle-right" />
                                                </span>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="section-head text-center">
                                <h2 data-title="Ürünler">Benzer Ürünler</h2>
                                <div className="sx-separator-outer  m-b30">
                                    <div className="sx-separator bg-primary" />
                                </div>
                            </div>
                            <div className="row">
                                {normalizedRelatedProducts.map((item, index) => (
                                    <div key={index} className="col-md-3 col-sm-6 col-xs-6 col-xs-100pc m-b30">
                                        <div className="sx-box sx-product-box overflow-hide">
                                            {item.sale ? <div className="shop-pro-sale-bnr">Firsat</div> : null}
                                            <div className="sx-thum-bx sx-img-overlay1">
                                                <img src={item.image} alt={item.title} />
                                                <div className="item-cart-view">
                                                    <div className="item-cart-btn">
                                                        <NavLink
                                                            to="/urun-detay"
                                                            state={{
                                                                categoryKey,
                                                                product: {
                                                                    image: item.image,
                                                                    imageUrls: item.imageUrls,
                                                                    title: item.title,
                                                                    code: item.code,
                                                                    description: item.description,
                                                                },
                                                                relatedProducts: normalizedRelatedProducts
                                                                    .filter((_, relatedIndex) => relatedIndex !== index)
                                                                    .slice(0, 4),
                                                            }}
                                                        >
                                                            <i className="fa fa-search" />
                                                        </NavLink>
                                                    </div>
                                                    <div className="item-cart-btn">
                                                        <NavLink to="/iletisim">
                                                            <i className="fa fa-phone" />
                                                        </NavLink>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="sx-info p-t20 text-center">
                                                <h5 className="sx-title">
                                                    <NavLink
                                                        to="/urun-detay"
                                                        state={{
                                                            categoryKey,
                                                            product: {
                                                                image: item.image,
                                                                imageUrls: item.imageUrls,
                                                                title: item.title,
                                                                code: item.code,
                                                                description: item.description,
                                                            },
                                                            relatedProducts: normalizedRelatedProducts
                                                                .filter((_, relatedIndex) => relatedIndex !== index)
                                                                .slice(0, 4),
                                                        }}
                                                    >
                                                        {item.title}
                                                    </NavLink>
                                                </h5>
                                                <p className="m-b0 product-code-text">
                                                    {item.code || '-'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <Footer />
            </>
        );
    }
}

const ShopDetailWithLocation = (props) => {
    const location = useLocation();
    return <ShopDetail {...props} location={location} />;
};

export default ShopDetailWithLocation;

